import React, { useEffect } from "react";
import { Field, ErrorMessage } from "formik";
import { Microphone, FastForward } from "phosphor-react";
import SpeechRecognition from "react-speech-recognition";
import Badge from 'react-bootstrap/Badge';
import CryptoJS from 'crypto-js';
import axiosConn from "config/axiosConn.js";
import { useDispatch } from 'react-redux'
import { sanitizeInputValue } from "../../utils/inputSanitizer";


const secretKey = 'nVhZ0HjU8tRfI1BqQ7wS6eP3kL2mX5cC4vNzJbW9sK6lM1oP2rF0dA7eG4iJ8aQ3sD';

const encryptData = (data, secretKey) => {
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), secretKey).toString();
  return encrypted;
};

const sanitizeFieldValue = (fieldName, fieldType, fieldValue, max) =>
  sanitizeInputValue(fieldValue, {
    name: fieldName,
    type: fieldType === "textarea3" ? "textarea" : fieldType,
    maxLength: max,
  });

const GenericField = ({
  name,
  label,
  nextRef, nextValueRef, resetRef, timer,
  value,
  saveField,
  placeholder,
  voiceSelected,
  innerRef,
  handleSpeak,
  handleFocusChange,
  setFieldValue,
  getActive,
  handleActive,
  langSelected,
  getActiveState,
  onBtnRef,
  skipFocusChange,
  skipBtn,
  listening,
  type,
  transcript, resetTranscript,
  required = false,
  isKeyBoardInput,
  handleSpeakWithoutVoice, disableRef, requiredBtn, disabled, initialValues, setInitialValues, size = 4, max
}) => {
  const micAnimation =
    getActiveState?.current === name && onBtnRef?.current ? "animate-mic" : "";

  const [showRequired, setShowRequired] = React.useState(true);
  const [isSkipBtnDisabled, setIsSkipBtnDisabled] = React.useState(required);
  const timerRef = React.useRef(null);
  const dispatch = useDispatch();
  const languages = [
    {
      name: "Hindi",
      short: "हि",
      code: "hi",
      literal: "हिन्दी",
      script: "Deva",
      isBeta: false
    },
    {
      name: "Telugu",
      short: "తె",
      code: "te",
      literal: "తెలుగు",
      script: "Telu",
      isBeta: false
    },
    {
      name: "Tamil",
      short: "த",
      code: "ta",
      literal: "தமிழ்",
      script: "Taml",
      isBeta: false
    },
    {
      name: "Kannada",
      short: "ಕ",
      code: "kn",
      literal: "ಕನ್ನಡ",
      script: "Knda",
      isBeta: false
    },
    {
      name: "Malayalam",
      code: "ml",
      literal: "മലയാളം",
      script: "Mlym",
      isBeta: false
    },
    {
      name: "Bangla",
      code: "bn",
      literal: "বাংলা",
      script: "Beng",
      isBeta: true
    },
    {
      name: "Marathi",
      code: "mr",
      literal: "मराठी",
      script: "Deva",
      isBeta: true
    },
    {
      name: "Gujarati",
      code: "gu",
      literal: "ગુજરાતી",
      script: "Gujr",
      isBeta: true
    },
    {
      name: "Odiya",
      code: "or",
      literal: "ଓଡ଼ିଆ",
      script: "Orya",
      isBeta: true
    },
    {
      name: "Punjabi",
      code: "pa",
      literal: "ਪੰਜਾਬੀ",
      script: "Guru",
      isBeta: true
    },
    {
      name: "Assamese",
      code: "as",
      literal: "অসমীয়া",
      script: "Beng",
      isBeta: true
    },
    {
      name: "English",
      code: "en",
      literal: "English",
      script: "Latn",
      isBeta: false
    }
  ];

  const delayedFocusChange = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      handleFocusChange(nextRef, nextValueRef, resetRef, timer);
      isKeyBoardInput.current = false;
    }, 5000);
  };

  const changeValues = async(name, value) => {
    if (name === 'contact_email' || name === 'email') return;
    if (langSelected === 'en-IN') {
      applySanitizedValue(value);
      return;
    }

    const data=encryptData(value, secretKey);
    try{
      
    

    const response = await axiosConn('post',`text-to-text`, {
        Text: data,
        fromScript: 'Latn',
        fromLanguage: "en-IN",
        toScript: languages.find(language => language.code === langSelected.replace("-IN", ""))?.code,
      }, {}, dispatch);

      const scriptedText = response.data[0]?.text;

      applySanitizedValue(scriptedText);
    }
    catch(error){
      console.log(error);
    }
  };

  useEffect(() => {
    if (getActiveState?.current === name) {
      if (transcript && transcript.length > 0 && getActive === name && listening) {
        setIsSkipBtnDisabled(false);
        applySanitizedValue(transcript);

        disableRef.current = false;
      } else {
        resetTranscript();
        onBtnRef.current = false;
      }
    }
  }, [getActive, name, setFieldValue, transcript]);

  const commands = [
    {
      command: 'slash',
      callback: () => { applySanitizedValue("/"); }
    },
    {
      command: 'skip',
      callback: () => { applySanitizedValue(""); handleFocusChange(nextRef, nextValueRef, resetRef, timer); }
    },
    // Add more commands as needed
  ];

  const handleSkipClick = () => {
    if (required && innerRef.current.value.trim().length === 0) {
      disableRef.current = true;
      stopListening();
      resetTranscript();
      onBtnRef.current = false;
      setShowRequired(true);
      handleSpeakWithoutVoice(getActiveState.current, langSelected, voiceSelected, getActiveState, requiredBtn);
    } else {
      skipFocusChange(nextRef, nextValueRef, handleFocusChange, resetRef, timer);
    }
  };

  useEffect(() => {
    onBtnRef.current = false;
    stopListening();
    resetTranscript();

    if (innerRef?.current.value.trim().length === 0 && required) {
      setIsSkipBtnDisabled(true);
    } else {
      setIsSkipBtnDisabled(false);
    }
    if (innerRef?.current.value.length === 0) {
      applySanitizedValue("");
      // setInitialValues({ ...initialValues, [name]: "" })
    }
  }, [getActiveState?.current]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (listening) {
        stopListening();
        resetTranscript();
        onBtnRef.current = false;
        if (!transcript) {
        } else {
          resetTranscript();
          delayedFocusChange();
        }
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [listening, skipFocusChange, onBtnRef, transcript]);

  const startListening = () => {
    SpeechRecognition.startListening({
      continuous: true,
      language: langSelected,
      commands: commands
    });
  };

  const BtnFunctionHandler = (e) => {
    if (getActiveState.current !== name) {
      getActiveState.current = name;
      handleSpeak(name);
    }

    if (onBtnRef.current && listening) {
      stopListening();
      onBtnRef.current = false;
      resetTranscript();
    } else {
      onBtnRef.current = true;
      startListening();
    }

    handleActive(e);
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
  };

  const applySanitizedValue = (rawValue) => {
    const safeValue = sanitizeFieldValue(name, type, rawValue, max);
    setFieldValue(name, safeValue);
    setInitialValues({ ...initialValues, [name]: safeValue });
    return safeValue;
  };

  return (
    <div style={{ position: "relative" }} className={`col-${size} form-group voiceformtop `}>
      <div
        onFocus={(e) => {
          stopListening();
          resetTranscript();
          handleActive(e);
          if (required && innerRef.current.value.trim().length === 0 && getActiveState.current === name) {
            disableRef.current = true;
          }
        }}
        className={`col-12 form-group ${getActive === name ? "active" : null} `}
      >
        <label htmlFor={name}>{label}
          {
            required && <span style={{ color: "red" }}>*</span>
          }
          {
            required && getActive === name && isSkipBtnDisabled && showRequired && (
              <Badge bg="danger" style={{
                position: "relative", top: "", left: "3px"
              }}>{requiredBtn}</Badge>
            )
          }</label>

        <div 
          className="formgroupIn" 
          onClick={() => innerRef.current && innerRef.current.focus()}
          style={{ cursor: 'pointer' }}
        >
          <Field
            disabled={disabled}
            onFocus={(e) => {
              if (getActiveState.current !== name) {
                getActiveState.current = name;
                handleSpeak(name);
              } else {
                handleSpeak(name);
              }
            }}
            style={{
              backgroundColor: required && getActive === name && isSkipBtnDisabled && showRequired ? "#fff5f5" : null,
              height: type === 'textarea3' ? '60px' : 'auto' // Example height
            }}
            
            innerRef={innerRef}
            name={name}
            // type="text"
            id={name}
            value={value}
            onBlur={(e) => {
              if (isKeyBoardInput.current) {
                changeValues(name, sanitizeFieldValue(name, type, e.target.value, max));
              }
            }}
            type={type}
            className="form-control"
            placeholder={placeholder}
            max={max}
            onChange={(e) => {
              let inputValue = sanitizeFieldValue(name, type, e.target.value, max);
              
              // Check if max is defined and if the input type is number
              if (max !== undefined && type === 'number' && inputValue !== '') {
                const numValue = Number(inputValue);
                if (numValue > max) {
                  // Don't allow values greater than max
                  return;
                }
              }
              
              if (innerRef.current.value.trim().length === 0 && required) {
                setIsSkipBtnDisabled(true);
              } else {
                setIsSkipBtnDisabled(false);
              }
              stopListening();
              resetTranscript();

              applySanitizedValue(inputValue);

              onBtnRef.current = false;
              if (innerRef.current.value.trim().length > 0) {
                disableRef.current = false;
              } else {
                disableRef.current = true;
              }
              isKeyBoardInput.current = true;
              delayedFocusChange();
            }}
          />

          {getActive === name && isSkipBtnDisabled && (
            <div
              className="skipBtn"
              style={{ cursor: "pointer", backgroundColor: "red", color: "white" }}
              onClick={handleSkipClick}
            >
              {skipBtn} <FastForward size={18} />
            </div>
          )}
          {getActive === name && !isSkipBtnDisabled && (
            <div
              className="skipBtn"
              style={{ cursor: "pointer" }}
              onClick={handleSkipClick}
            >
              {skipBtn} <FastForward size={18} />
            </div>
          )}

          <div className="micIcon">
            <span className={`inptIcn ${micAnimation === 'animate-mic' ? 'activate-mic' : ""}`} >
              <Microphone
                onClick={(e) => {
                  if (disableRef.current === false && !required) {
                    BtnFunctionHandler(e);
                    // Check if the input field already has focus
                    if (getActiveState.current !== innerRef.current) {
                      innerRef.current.focus();
                    }
                  }
                  if (required) {
                    BtnFunctionHandler(e);
                    // Check if the input field already has focus
                    if (getActiveState.current !== innerRef.current) {
                      innerRef.current.focus();
                    }
                  }
                }}
                className={`mic-icon2  ${micAnimation}`}
                size={34}
              />
            </span>
          </div>
        </div>
        <ErrorMessage name={name} component="div" />
      </div>
    </div>
  );
};

export default GenericField;
